import { CVTilesLayer } from '@aus-platform/cesium';
import { Cesium3DTileset } from 'cesium';
import { isNil, sortBy, times } from 'lodash';
import {
  type CesiumProxy,
  type MapLayer,
} from '../../../../../../../shared/cesium';
import {
  type WorkspaceLayer,
  type WorkspaceLayerListObj,
} from '../../../../shared';
import {
  LayerSource,
  LayerType,
  type WorkspaceDSMLayer,
  type WorkspaceResponseLayer,
} from '../../../../../../../shared/api';
import type {
  CombinedLayer,
  CombinedWorkspaceLayer,
  CombinedDSMLayer,
} from 'map-3d/types';
import type {
  CesiumReorderIndices,
  LayerCesiumIndexMap,
  LayerZIndexMap,
  ProcessedWorkspaceLayers,
} from '../types';

/**
 * Removes a map layer from Cesium based on its type
 * @param cesiumProxy Cesium proxy instance
 * @param mapLayer Map layer to remove (either point cloud or imagery layer)
 */
export const removeMapLayer = (
  cesiumProxy: CesiumProxy,
  mapLayer: MapLayer,
): void => {
  if (mapLayer instanceof CVTilesLayer) {
    cesiumProxy.layerManager.removeCadLayer(mapLayer);
  } else if (mapLayer instanceof Cesium3DTileset) {
    cesiumProxy.layerManager.removePointCloudLayer(mapLayer);
  } else {
    cesiumProxy.layerManager.removeImageryLayers([mapLayer]);
  }
};

const isNonImageryLayer = (type: LayerType) => {
  return type === LayerType.PointCloud || type === LayerType.Cad;
};

/**
 * Counts the number of non-imagery layers in a range of layers
 * @param layers Array of workspace layers
 * @param start Start index
 * @param end End index
 * @returns Number of non-imagery layers in the range
 */
const countNonImageryLayersInRange = (
  layers: WorkspaceLayer[],
  start: number,
  end: number,
): number => {
  const minIndex = Math.min(start, end);
  const maxIndex = Math.max(start, end);

  let count = 0;
  for (let i = minIndex; i <= maxIndex; i++) {
    if (layers[i] && isNonImageryLayer(layers[i].type)) {
      count++;
    }
  }
  return count;
};

/**
 * Converts Redux layer indices to Cesium imagery layer indices for reordering operations
 * @param layers Array of all workspace layers
 * @param sourceIndex Source index in Redux array
 * @param destinationIndex Destination index in Redux array
 * @returns Object with Cesium source and destination indices, and filtered imagery layers
 */
export const getCesiumIndicesFromReduxIndices = (
  layers: WorkspaceLayer[],
  sourceIndex: number,
  destinationIndex: number,
): CesiumReorderIndices => {
  // Point cloud and CAD layers don't participate in Cesium imagery reordering.
  if (layers[sourceIndex] && isNonImageryLayer(layers[sourceIndex].type)) {
    return {
      cesiumSourceIndex: undefined,
      cesiumDestinationIndex: undefined,
    };
  }

  const nonImageryLayerCount = countNonImageryLayersInRange(
    layers,
    sourceIndex,
    destinationIndex,
  );

  const cesiumSourceIndex =
    sourceIndex < destinationIndex
      ? sourceIndex
      : sourceIndex - nonImageryLayerCount;

  const cesiumDestinationIndex =
    sourceIndex < destinationIndex
      ? destinationIndex - nonImageryLayerCount
      : destinationIndex;

  return {
    cesiumSourceIndex,
    cesiumDestinationIndex,
  };
};

/**
 * Processes workspace layers from backend response to assign proper Redux and Cesium indices
 * @param workspaceLayers Array of workspace layers from backend
 * @param dsmLayers Array of DSM layers from backend
 * @returns Object containing processed layers with proper indices
 */
export const processWorkspaceLayersForLoading = (
  workspaceLayers: WorkspaceResponseLayer[],
  dsmLayers: WorkspaceDSMLayer[],
): ProcessedWorkspaceLayers => {
  // Combine all layers and sort by zIndex.
  const allLayers: CombinedLayer[] = sortBy(
    [
      ...workspaceLayers.map(
        (layer): CombinedWorkspaceLayer => ({
          ...layer,
          layerSource: LayerSource.Workspace,
        }),
      ),
      ...dsmLayers.map(
        (layer): CombinedDSMLayer => ({
          ...layer,
          type: LayerType.CapturedDsm,
          layerSource: LayerSource.Dsm,
        }),
      ),
    ],
    'zIndex',
  );

  const reduxIndices: LayerZIndexMap = {};
  const cesiumIndices: LayerCesiumIndexMap = {};

  let cesiumIndex = 1;

  allLayers.forEach((layer, reduxIndex) => {
    const layerId = layer.id;

    // Redux index is simply the position in the sorted array.
    reduxIndices[layerId] = reduxIndex + 1;

    if (isNonImageryLayer(layer.type)) {
      // Point cloud and CAD layers don't get Cesium imagery indices.
      cesiumIndices[layerId] = undefined;
    } else {
      cesiumIndices[layerId] = cesiumIndex;
      cesiumIndex++;
    }
  });

  return {
    allLayers,
    reduxIndices,
    cesiumIndices,
  };
};

/**
 * Calculates cesium indices for all layers based on their position and type
 * @param layers Array of workspace layers in display order
 * @returns Record mapping layer IDs to their cesium indices
 */
const calculateCesiumIndicesForLayers = (
  layers: WorkspaceLayer[],
): LayerCesiumIndexMap => {
  const cesiumIndices: LayerCesiumIndexMap = {};

  const imageryLayersCount = layers.filter(
    (layer) => !isNonImageryLayer(layer.type),
  ).length;
  let cesiumIndex = imageryLayersCount;

  layers.forEach((layer) => {
    if (isNonImageryLayer(layer.type)) {
      cesiumIndices[layer.id] = undefined;
    } else {
      cesiumIndices[layer.id] = cesiumIndex;
      // Decrement for reverse order.
      cesiumIndex--;
    }
  });

  return cesiumIndices;
};

/**
 * Updates z-indices and cesium indices for workspace layers in reverse order
 * @param layers Array of workspace layers
 * @param updateLayer Function to update layer in Redux store
 */
export const updateWorkspaceLayerZIndices = (
  layers: WorkspaceLayer[],
  updateLayer: (layerListObj: WorkspaceLayerListObj) => void,
): void => {
  // Calculate cesium indices for all layers.
  const cesiumIndices = calculateCesiumIndicesForLayers(layers);

  // Iterate through each layer to update its z-index and cesium index.
  layers.forEach((layer, index) => {
    const zIndex = layers.length - index;

    updateLayer({
      [layer.id]: {
        ...layer,
        zIndex,
        cesiumIndex: cesiumIndices[layer.id],
      },
    });
  });
};

/**
 * Reorders map layers in Cesium based on Redux layer reordering
 * @param cesiumProxy Cesium proxy instance
 * @param layers Array of workspace layers
 * @param sourceIndex Source index in Redux array
 * @param destinationIndex Destination index in Redux array
 */
export const reorderMapLayers = (
  cesiumProxy: CesiumProxy,
  layers: WorkspaceLayer[],
  sourceIndex: number,
  destinationIndex: number,
): void => {
  const sourceLayer = layers[sourceIndex];

  const { mapLayer } = sourceLayer;

  // Early return if no map layer, point cloud, or CAD layer (no Cesium imagery reordering needed).
  if (
    !mapLayer ||
    mapLayer instanceof Cesium3DTileset ||
    mapLayer instanceof CVTilesLayer
  ) {
    return;
  }

  // Get Cesium indices for imagery layers only.
  const { cesiumSourceIndex, cesiumDestinationIndex } =
    getCesiumIndicesFromReduxIndices(layers, sourceIndex, destinationIndex);

  // Early return if either index is undefined (no Cesium reordering needed).
  if (
    isNil(cesiumSourceIndex) ||
    isNil(cesiumDestinationIndex) ||
    !cesiumProxy
  ) {
    return;
  }

  const difference = Math.abs(cesiumDestinationIndex - cesiumSourceIndex);
  const isMovingUp = cesiumDestinationIndex > cesiumSourceIndex;

  // Move the layer step by step to reach the target position.
  times(difference, () => {
    if (isMovingUp) {
      // Moving to higher index = more visible = lower the layer.
      cesiumProxy.layerManager.lowerImageryLayer(mapLayer);
    } else {
      // Moving to lower index = less visible = raise the layer.
      cesiumProxy.layerManager.raiseImageryLayer(mapLayer);
    }
  });
};
